import React from 'react';

interface ProjectCostItem {
  project_id: string;
  project_name: string;
  client_name?: string;
  infrastructure_budget: string | number;
  infrastructure_spent: string | number;
  infrastructure_remaining: string | number;
  status: string;
  cost_tracking_enabled?: boolean;
}

interface ProjectCostBreakdownProps {
  data: ProjectCostItem[] | null;
  loading: boolean;
}

const ProjectCostBreakdown: React.FC<ProjectCostBreakdownProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Project Cost Breakdown
        </h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  const projects = data || [];

  const formatCurrency = (amount: string | number | null | undefined) => {
    if (amount == null) return 'N/A';
    return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'on_track':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'over_budget':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'on_track':
        return 'On Track';
      case 'warning':
        return 'Warning';
      case 'over_budget':
        return 'Over Budget';
      default:
        return status;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Project Cost Breakdown
      </h3>

      {projects.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No project cost data available
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Budget
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Spent
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Remaining
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Burn Rate
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {projects.map((project) => {
                // Calculate infrastructure budget utilization for FinOps view
                const infraBudget = Number.parseFloat(String(project.infrastructure_budget)) || 0;
                const infraSpent = Number.parseFloat(String(project.infrastructure_spent)) || 0;
                const budgetUtilization = infraBudget > 0 ? (infraSpent / infraBudget) * 100 : 0;

                return (
                  <tr key={project.project_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-4 text-sm">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {project.project_name}
                        </div>
                        {project.client_name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {project.client_name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400">
                      {formatCurrency(project.infrastructure_budget)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-right">
                      <div className="text-gray-900 dark:text-white font-medium">
                        {formatCurrency(project.infrastructure_spent)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400">
                      {formatCurrency(project.infrastructure_remaining)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${
                              budgetUtilization >= 100
                                ? 'bg-red-500'
                                : budgetUtilization >= 80
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(budgetUtilization, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {budgetUtilization.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(project.status)}`}>
                        {getStatusLabel(project.status)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectCostBreakdown;
